import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { adminApi } from '../api';
import { User, KeyRound, Plus, RefreshCw, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import PostCreateClientDialog from './PostCreateClientDialog';

const genPass = () => Math.random().toString(36).slice(2, 10);

const CreateClientDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({ name: '', password: genPass(), note: '', email: '', phone_wa: '', send_email: true, send_whatsapp: true });
  const [busy, setBusy] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Nome cliente obbligatorio');
    if (!form.password.trim()) return toast.error('Password obbligatoria');
    setBusy(true);
    try {
      const c = await adminApi.createClient({
        name: form.name.trim(),
        password: form.password.trim(),
        note: form.note.trim(),
        email: form.email.trim(),
        phone_wa: form.phone_wa.trim(),
        send_email: form.send_email && !!form.email.trim(),
        send_whatsapp: false, // wa_link is still generated on backend based on phone_wa, but we don't auto-open
      });
      // If phone provided, ask backend for wa_link (we passed send_whatsapp=false so it's empty).
      // Fallback: build wa_link client-side from phone + credentials to feed the post-create dialog.
      let waLink = c.wa_link || '';
      if (!waLink && form.phone_wa.trim()) {
        const digits = form.phone_wa.replace(/\D/g, '');
        if (digits) {
          const link = `${window.location.origin}/c/${c.slug}`;
          const loginId = form.email.trim() || c.name;
          const msg = `Ciao ${c.name},\n\nLe tue foto sono pronte nella tua area riservata FRAMEBOX by Nicola Verardo.\n\nLink: ${link}\nNome: ${loginId}\nPassword: ${form.password}\n\nA presto,\nNicola`;
          waLink = `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
        }
      }
      toast.success(`Cliente "${c.name}" creato`);
      setCreatedInfo({
        client: { ...c, __password: form.password, phone_wa: form.phone_wa.trim() },
        waLink,
        emailSent: c.email_sent,
        emailError: c.email_error,
      });
      onOpenChange(false);
      setPostOpen(true);
      onCreated && await onCreated();
      setForm({ name: '', password: genPass(), note: '', email: '', phone_wa: '', send_email: true, send_whatsapp: true });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore creazione'); }
    finally { setBusy(false); }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Nuovo cliente</DialogTitle>
          <DialogDescription>Crea un cliente e scegli se inviare le credenziali via Email e/o WhatsApp.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Nome cliente</span>
            <div className="mt-1 flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-[#C9A961] focus-within:ring-2 focus-within:ring-[#F7F1E1]">
              <User className="w-4 h-4 text-slate-400" />
              <input required data-testid="new-client-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} placeholder="Es. Andrea e Dalila, Studio Sneark..." className="flex-1 outline-none bg-transparent" />
            </div>
            <span className="block text-[11px] text-slate-500 mt-1">L'URL sarà generato automaticamente da questo nome</span>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Email (opzionale)</span>
              <div className="mt-1 flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-[#C9A961] focus-within:ring-2 focus-within:ring-[#F7F1E1]">
                <Mail className="w-4 h-4 text-slate-400" />
                <input type="email" data-testid="new-client-email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} placeholder="nome@esempio.it" className="flex-1 outline-none bg-transparent text-sm" />
              </div>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">WhatsApp (opzionale)</span>
              <div className="mt-1 flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-[#C9A961] focus-within:ring-2 focus-within:ring-[#F7F1E1]">
                <Phone className="w-4 h-4 text-emerald-500" />
                <input type="tel" data-testid="new-client-phone" value={form.phone_wa} onChange={e=>setForm({...form, phone_wa:e.target.value})} placeholder="+39 333 1234567" className="flex-1 outline-none bg-transparent text-sm" />
              </div>
              <span className="block text-[11px] text-slate-500 mt-1">Numero con prefisso internazionale (+39, ecc.)</span>
            </label>
          </div>

          {/* Send channels */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-3 space-y-2">
            <span className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">Alla creazione</span>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" data-testid="new-client-send-email" checked={form.send_email} onChange={e=>setForm({...form, send_email:e.target.checked})} disabled={!form.email.trim()} className="accent-[#C9A961] w-4 h-4" />
              <Mail className="w-4 h-4 text-slate-500" />
              <span className={`text-sm ${!form.email.trim() ? 'text-slate-400' : 'text-slate-700'}`}>Invia email automatica {!form.email.trim() && <span className="text-[11px] italic">(inserisci email sopra)</span>}</span>
            </label>
            <p className="text-[11px] text-slate-500 pl-6">Per WhatsApp userai un pulsante dedicato dopo la creazione — nessun invio automatico.</p>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">Password per il cliente</span>
            <div className="mt-1 flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-[#C9A961] focus-within:ring-2 focus-within:ring-[#F7F1E1]">
              <KeyRound className="w-4 h-4 text-slate-400" />
              <input required data-testid="new-client-password" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} className="flex-1 outline-none bg-transparent font-mono" />
              <button type="button" onClick={()=>setForm({...form, password: genPass()})} className="text-slate-500 hover:text-[#B8944B] p-1" title="Genera password"><RefreshCw className="w-4 h-4" /></button>
            </div>
            <span className="block text-[11px] text-slate-500 mt-1">La password viene salvata cifrata. Segnala in un posto sicuro.</span>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">Note interne (opzionale)</span>
            <textarea value={form.note} onChange={e=>setForm({...form, note:e.target.value})} rows={2} placeholder="Es. Matrimonio 10 agosto 2025..." className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-[#C9A961] focus:ring-2 focus:ring-[#F7F1E1] resize-none text-sm" />
          </label>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={()=>onOpenChange(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Annulla</button>
            <button data-testid="new-client-submit" disabled={busy} className="inline-flex items-center gap-2 bg-black hover:bg-[#C9A961] hover:text-black text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"><Plus className="w-4 h-4" /> {busy ? 'Creazione...' : 'Crea cliente'}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>

    <PostCreateClientDialog
      open={postOpen}
      onOpenChange={setPostOpen}
      client={createdInfo?.client}
      waLink={createdInfo?.waLink}
      emailSent={createdInfo?.emailSent}
      emailError={createdInfo?.emailError}
    />
    </>
  );
};

export default CreateClientDialog;
